import React, { useState } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import { CheckCircle, Warning, Spinner, Lightning, ClipboardText, Check } from '@phosphor-icons/react';
import { ConcernAnalysisSection } from '../concern-analysis/ConcernAnalysisSection';
import { LegalPanelSection } from '../legal-panel/LegalPanelSection';

const urgencyClass = (urgency) => {
  if (urgency === 'high') return 'bg-red-50 text-red-700';
  if (urgency === 'medium') return 'bg-yellow-50 text-yellow-700';
  return 'bg-blue-50 text-blue-700';
};

export const OverviewTab = ({ caseData, risk, generateSummary, generatingSummary, caseId, loadAll }) => {
  const [checklist, setChecklist] = useState(caseData.checklist || []);

  const toggleChecklist = async (idx) => {
    try {
      const { data } = await api.put(`/cases/${caseData._id}/checklist/${idx}`);
      setChecklist(data.checklist);
    } catch (err) {
      logger.error('Toggle checklist failed:', err);
    }
  };

  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-outfit text-lg font-semibold text-[#09090B]">AI Case Summary</h3>
          <button data-testid="generate-summary-button" onClick={generateSummary} disabled={generatingSummary}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#002FA7] text-white font-ibm rounded-md hover:bg-[#00237D] disabled:opacity-50 transition-all">
            {generatingSummary ? <><Spinner size={14} className="animate-spin" /> Generating...</> : <><Lightning size={14} /> Generate Summary</>}
          </button>
        </div>
        {caseData.ai_summary ? (
          <p className="text-sm text-[#09090B] font-ibm leading-relaxed whitespace-pre-wrap" data-testid="case-summary-text">{caseData.ai_summary}</p>
        ) : (
          <p className="text-sm text-[#6B7280] font-ibm italic">No summary generated yet. Upload and analyze documents first, then generate a summary.</p>
        )}
      </div>
      {caseData.key_findings?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
          <h3 className="font-outfit text-lg font-semibold text-[#09090B] mb-3">Key Findings</h3>
          <ul className="space-y-2">
            {caseData.key_findings.map((f, i) => (
              <li key={`finding-${i}`} className="flex items-start gap-2 text-sm font-ibm text-[#09090B]">
                <CheckCircle size={16} className="text-[#34C759] mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <h3 className="font-outfit text-lg font-semibold text-[#09090B] mb-4">Risk Indicators</h3>
        <div className="grid grid-cols-3 gap-4">
          {[
            { key: 'retaliation', label: 'Retaliation Risk', color: '#FF3B30' },
            { key: 'procedural', label: 'Procedural Issues', color: '#FFCC00' },
            { key: 'evidentiary_gaps', label: 'Evidentiary Gaps', color: '#002FA7' },
          ].map(r => (
            <div key={r.key} className="text-center">
              <div className="text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] font-ibm mb-2">{r.label}</div>
              <div className="relative h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                <div className="absolute left-0 top-0 h-full rounded-full transition-all duration-500" style={{ width: `${(risk[r.key] || 0) * 10}%`, backgroundColor: r.color }} />
              </div>
              <div className="font-outfit text-xl font-bold mt-1" style={{ color: r.color }}>{risk[r.key] || 0}/10</div>
            </div>
          ))}
        </div>
      </div>
      {caseData.ai_next_steps?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
          <h3 className="font-outfit text-lg font-semibold text-[#09090B] mb-3">Recommended Next Steps</h3>
          <div className="space-y-3">
            {caseData.ai_next_steps.map((s, i) => (
              <div key={`next-step-${i}`} className="flex items-start gap-3 p-3 bg-[#F9FAFB] rounded-md border border-[#E5E7EB]">
                <span className={`text-xs font-bold px-2 py-0.5 rounded shrink-0 mt-0.5 ${urgencyClass(s.urgency)}`}>
                  {s.urgency?.toUpperCase()}
                </span>
                <div>
                  <p className="text-sm font-ibm font-semibold text-[#09090B]">{s.step}</p>
                  {s.rationale && <p className="text-xs text-[#6B7280] font-ibm mt-0.5">{s.rationale}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Document Checklist */}
      {checklist.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
          <h3 className="font-outfit text-lg font-semibold text-[#09090B] mb-3 flex items-center gap-2"><ClipboardText size={20} /> Document Checklist</h3>
          <p className="text-xs text-[#6B7280] font-ibm mb-3">{checklist.filter(c=>c.completed).length}/{checklist.length} completed</p>
          <div className="space-y-2">
            {checklist.map((c, i) => (
              <button key={`checklist-${i}`} onClick={() => toggleChecklist(i)} data-testid={`checklist-item-${i}`}
                className={`flex items-center gap-3 w-full text-left p-2.5 rounded-md border transition-all ${c.completed ? 'bg-green-50/50 border-green-100' : 'bg-white border-[#E5E7EB] hover:bg-[#F9FAFB]'}`}>
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${c.completed ? 'bg-[#34C759] border-[#34C759]' : 'border-[#E5E7EB]'}`}>
                  {c.completed && <Check size={12} weight="bold" className="text-white" />}
                </div>
                <span className={`text-sm font-ibm ${c.completed ? 'text-[#6B7280] line-through' : 'text-[#09090B]'}`}>{c.item}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Common Patterns */}
      {caseData.common_patterns?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
          <h3 className="font-outfit text-lg font-semibold text-[#09090B] mb-3">Common Patterns to Watch For</h3>
          <div className="space-y-2">
            {caseData.common_patterns.map((p, i) => (
              <div key={`pattern-${i}`} className="flex items-start gap-2 p-2.5 bg-[#F9FAFB] rounded-md border border-[#E5E7EB]">
                <Warning size={14} className="text-[#FFCC00] mt-0.5 shrink-0" />
                <span className="text-sm font-ibm text-[#09090B]">{p}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Concern-Focused Analysis */}
      <ConcernAnalysisSection caseId={caseId} caseData={caseData} loadAll={loadAll} />
      {/* Legal Panel: Applicable Laws & Government Agencies */}
      <LegalPanelSection caseId={caseId} caseData={caseData} loadAll={loadAll} />
    </div>
  );
};
